import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/queue`;
export const WS_DISPLAY_URL = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/display`;

// Il cookie di sessione è HttpOnly: viaggia da solo, non è leggibile da JavaScript.
export const api = axios.create({ baseURL: API, withCredentials: true });

let uscitaInCorso = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const fuori = err?.response?.status === 401;
    const suLogin = window.location.pathname.startsWith("/login");
    if (fuori && !suLogin && !uscitaInCorso) {
      uscitaInCorso = true;
      window.location.replace("/login?scaduta=1");
    }
    return Promise.reject(err);
  }
);

export const logout = async () => {
  try {
    await api.post("/auth/logout");
  } finally {
    window.location.replace("/login");
  }
};

export const SALE = {
  levi: { key: "levi", label: "Sala Levi Montalcini", short: "Levi Montalcini", desc: "Visita medica" },
  jenner: { key: "jenner", label: "Sala Jenner", short: "Jenner", desc: "Prelievo e accertamenti" },
};

export const ESAMI = {
  A: "Alcol",
  D: "Droghe",
  P: "Protocollo",
  U: "Urine",
  C: "Carbossiemoglobina",
  T: "Tetano",
};
export const ESAMI_EXTRA = { ECG: "ECG", SPIRO: "Spirometria", AUDIO: "Audiometria", VISIVO: "Visiotest" };
// Esami che impongono il passaggio in Sala Jenner (stessa regola del backend).
export const ESAMI_JENNER = ["A", "D", "P", "U", "C", "T", "ECG", "SPIRO"];

export const prefisso = (p) => (p.ambulatorio && p.visita ? "AV" : p.ambulatorio ? "A" : "V");

export const saleRichieste = (p) => [...(p.visita ? ["levi"] : []), ...(p.ambulatorio ? ["jenner"] : [])];

export const prestazioneLabel = (p) => saleRichieste(p).map((s) => SALE[s].short).join(" + ");

export const esamiAttivi = (p) =>
  Object.entries(p.esami || {}).filter(([, v]) => v).map(([k, v]) => ({
    key: k,
    label: ESAMI[k] || ESAMI_EXTRA[k] || k,
    valore: v,
  }));

export const PRESTAZIONE_STYLE = {
  A: { label: "Solo Jenner", badge: "bg-cyan-700 text-white", soft: "bg-cyan-50 border-cyan-200 text-cyan-800" },
  V: { label: "Solo Levi Montalcini", badge: "bg-violet-700 text-white", soft: "bg-violet-50 border-violet-200 text-violet-800" },
  AV: { label: "Jenner + Levi", badge: "bg-teal-700 text-white", soft: "bg-teal-50 border-teal-200 text-teal-800" },
};

export const SALA_STYLE = {
  levi: { text: "text-violet-300", dot: "bg-violet-400", card: "border-violet-500/30 bg-violet-500/[0.07]", number: "text-violet-300" },
  jenner: { text: "text-cyan-300", dot: "bg-cyan-400", card: "border-cyan-500/30 bg-cyan-500/[0.07]", number: "text-cyan-300" },
};

export const styleOf = (p) => PRESTAZIONE_STYLE[prefisso(p)];

export const STATO_STYLE = {
  da_accettare: { label: "Da accettare", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  chiamato: { label: "In corso", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  completato: { label: "Completato", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  assente: { label: "Assente", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

export const STATO_SALA_STYLE = {
  in_attesa: "bg-white/70 border-slate-200 text-slate-600",
  in_corso: "bg-emerald-600 text-white border-emerald-600",
  completato: "bg-slate-100 border-slate-200 text-slate-400 line-through",
  assente: "bg-rose-100 border-rose-200 text-rose-700",
};

export const EVENTO_LABEL = {
  accettazione: "Accettazione confermata",
  codice: "Codice generato",
  chiamata: "Chiamata",
  annullo_chiamata: "Chiamata annullata",
  email: "Promemoria e-mail inviato",
  completamento: "Completato",
  assenza: "Segnato assente",
  ripristino: "Rimesso in attesa",
  riapertura: "Rimesso in coda (completamento annullato)",
  richiamo: "Richiamato a voce in sala",
  modifica: "Dati modificati",
};

// ---- Tempi di visita: arrivo, inizio/fine per sala, attesa e totale in studio ----
const minutiTra = (da, a) => {
  if (!da || !a) return null;
  const diff = Math.round((new Date(a) - new Date(da)) / 60000);
  return Number.isFinite(diff) && diff >= 0 ? diff : null;
};

const soloOra = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Rome",
      })
    : "";

export const tempiVisita = (p) => {
  const tempi = p.tempi || {};
  const sale = {};
  const inizi = [];
  const fini = [];
  const durate = [];
  saleRichieste(p).forEach((r) => {
    const t = tempi[r] || {};
    const durata = minutiTra(t.inizio, t.fine);
    sale[r] = { inizio: soloOra(t.inizio), fine: soloOra(t.fine), durata };
    if (t.inizio) inizi.push(t.inizio);
    if (t.fine) fini.push(t.fine);
    if (durata !== null) durate.push(durata);
  });
  const primo = inizi.length ? inizi.slice().sort()[0] : null;
  const ultimo = fini.length ? fini.slice().sort().pop() : null;
  return {
    arrivo: p.orario_arrivo || "",
    inizio: soloOra(primo),
    fine: soloOra(ultimo),
    sale,
    attesa: minutiTra(p.accettato_at, primo),
    visita: durate.length ? durate.reduce((a, b) => a + b, 0) : null,
    totale: minutiTra(p.accettato_at, ultimo),
  };
};

export const medieTempi = (patients) => {
  const calcolati = (patients || []).map(tempiVisita);
  const media = (valori) => {
    const v = valori.filter((x) => x !== null && x !== undefined);
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  };
  return {
    attesa: media(calcolati.map((t) => t.attesa)),
    visita: media(calcolati.map((t) => t.visita)),
    totale: media(calcolati.map((t) => t.totale)),
    sale: Object.fromEntries(
      Object.keys(SALE).map((r) => [r, media(calcolati.map((t) => t.sale[r]?.durata ?? null))])
    ),
    rilevati: calcolati.filter((t) => t.totale !== null).length,
  };
};

export const minuti = (v) => (v === null || v === undefined ? "—" : `${v} min`);

export const openPlannerModello = () => window.open(`${API}/planner/modello`, "_blank");

const CF_REGEX = /^[A-Z]{6}\d{2}[ABCDEHLMPRST]\d{2}[A-Z]\d{3}[A-Z]$/;
const CF_PARI = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const CF_DISPARI = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
const CF_RESTO = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Verifica formato + carattere di controllo. Restituisce null se valido, altrimenti il messaggio.
export const validaCodiceFiscale = (cf) => {
  const valore = (cf || "").trim().toUpperCase();
  if (!valore) return null;
  if (valore.length !== 16) return `Codice fiscale di ${valore.length} caratteri: devono essere 16`;
  if (!CF_REGEX.test(valore)) return "Formato non valido (6 lettere, 2 cifre, 1 lettera mese, 2 cifre, 1 lettera, 3 cifre, 1 lettera)";
  let somma = 0;
  for (let i = 0; i < 15; i++) {
    const idx = CF_PARI.indexOf(valore[i]);
    if (idx < 0) return "Caratteri non ammessi nel codice fiscale";
    const n = idx >= 26 ? idx - 26 : idx; // lettere A=0..Z=25, cifre 0..9
    somma += (i + 1) % 2 === 0 ? n : CF_DISPARI[n];
  }
  const atteso = CF_RESTO[somma % 26];
  if (valore[15] !== atteso) return `Carattere di controllo errato (atteso ${atteso})`;
  return null;
};

export const openConsenso = (p) => window.open(`${API}/patients/${p.id}/consenso`, "_blank");
export const openScheda = (p) => window.open(`${API}/patients/${p.id}/scheda`, "_blank");
export const openReportPdf = (giorno) => window.open(`${API}/report/pdf?data=${giorno}`, "_blank");
export const openReportExcel = (giorno) => window.open(`${API}/report/excel?data=${giorno}`, "_blank");

export const LOGO_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_studio-manager-app-2/artifacts/e938ifxp_LOGO%20MAB%20NERO%20PNG.png";

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const activeDay = (patients) => {
  const today = todayISO();
  const giorni = [...new Set(patients.map((p) => p.data || today))].sort();
  if (giorni.length === 0 || giorni.includes(today)) return today;
  return giorni[0];
};

// Ricerca libera su qualunque parametro del paziente, senza vincoli di data.
export const matchPatient = (p, query) => {
  const termini = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (termini.length === 0) return true;
  const blob = [
    p.nome, p.cognome, p.azienda, p.mansione, p.tipo_visita, p.codice, p.codice_fiscale,
    p.email, p.luogo, p.nazionalita, p.referente, p.contatto, p.data, p.orario, p.orario_arrivo,
    p.note, p.data_nascita, prestazioneLabel(p), STATO_STYLE[p.stato]?.label,
    ...esamiAttivi(p).flatMap((e) => [e.key, e.label, e.valore]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return termini.every((t) => blob.includes(t));
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  });
};
